import { makeAutoObservable } from "mobx";
import { ExtendedShareholderDTO, isOwnShareholder } from "@/types/Shareholder";
import Big from "big.js";
import {
  CalculationMode,
  dateBefore,
  TransactionDateCardModel,
} from "@/shareholderRegister/transactions/commonTransactionsDomain";
import { CancellationOfShares, CancellationOfSharesDTO, Transaction } from "@/types/Transactions";
import { combineDateAndTime } from "@/utils/Dates";
import {
  createPurposeModel,
  Purpose,
} from "@/shareholderRegister/transactions/commonCapitalReduction";
import { TransactionType } from "@/types/enums/TransactionType";
import { TMessage } from "@/i18n/tt";
import { LocalDateFieldModel, NumberFieldModel, RadioFieldModel } from "@/common/commonForms";
import { isPending, Result } from "@/common/common";
import { ShareType, ShareTypeCode } from "@/common/share";
import { TransactionStore } from "@/stores/TransactionStore";
import { SelectedActor } from "@/types/SelectActors";
import { toShareholderReference } from "@/utils/UrnReference";
import { identity, sumBy } from "lodash";
import { hasShares } from "@/stores/ShareholdersStore.ts";

export enum SharesEntryMode {
  MANUAL = "MANUAL",
  PER_SHAREHOLDER = "PER_SHAREHOLDER",
}

export enum ShareholderEntryMode {
  MANUAL = "MANUAL",
  PRO_RATA = "PRO_RATA",
}

function restoreCalculationMode(transactionData?: CancellationOfSharesDTO): CalculationMode {
  if (!transactionData) return CalculationMode.AUTO;
  return transactionData.isAutoCalculate ? CalculationMode.AUTO : CalculationMode.MANUAL;
}

export class CancellationOfSharesModel {
  purposeModel: RadioFieldModel<Purpose | undefined>;
  sharesEntryMode: RadioFieldModel<SharesEntryMode>;
  shareholderEntryMode: RadioFieldModel<ShareholderEntryMode>;
  calculationMode: RadioFieldModel<CalculationMode>;
  shareTypeSpecifications: ShareTypeSpecificationModel[] = [];
  cancellationForms: ShareholderCancellationModel[] = [];
  resolutionDate: LocalDateFieldModel;
  isRegisteredInBR = false;
  isDirty = false;
  shareTypeSpecificationsError: TMessage[] = [];
  transactionDateCardModel: TransactionDateCardModel;

  constructor(
    readonly transactionStore: TransactionStore,
    readonly companyData: { orgNumber: string },
    readonly shareholderData: {
      shareTypesWithShares: ShareType[];
      shareSumBySharetypeCode: { [key in ShareTypeCode]?: number };
      shareholders: ExtendedShareholderDTO[];
      currency: string;
      transactionLog: Transaction[];
      loadShareholdersForDate: (orgNumber: string, date: string) => unknown;
      shareholdersResult: Result;
      canAutoCalculatePayment: boolean;
    },
    readonly transactionData?: CancellationOfSharesDTO,
  ) {
    this.purposeModel = createPurposeModel({
      transactionType: TransactionType.CANCELLATION_OF_SHARES,
      onChange: (value) => this.changePurpose(value),
      value: transactionData?.shareReductionPurpose as Purpose | undefined,
    });
    this.sharesEntryMode = new RadioFieldModel<SharesEntryMode>({
      value: SharesEntryMode.MANUAL,
      options: [SharesEntryMode.MANUAL, SharesEntryMode.PER_SHAREHOLDER],
      idFn: identity,
      labelFn: (mode) => ({ key: `transactions.capitalReduction.sharesEntryMode.${mode}` }),
      disabledFn: (mode) =>
        mode === SharesEntryMode.PER_SHAREHOLDER && !this.isManualShareholderEntry,
      onChange: (mode) => {
        this.setDirty();
        if (mode === SharesEntryMode.PER_SHAREHOLDER) this.syncAllSharetypeSpecs();
      },
    });
    this.shareholderEntryMode = new RadioFieldModel<ShareholderEntryMode>({
      value: transactionData?.isCancellationProRata
        ? ShareholderEntryMode.PRO_RATA
        : ShareholderEntryMode.MANUAL,
      options: () =>
        this.purposeModel.value === Purpose.CANCELLATION_OF_OWN
          ? [ShareholderEntryMode.MANUAL]
          : [ShareholderEntryMode.MANUAL, ShareholderEntryMode.PRO_RATA],
      idFn: identity,
      labelFn: (mode) => ({ key: `transactions.capitalReduction.shareholderEntryMode.${mode}` }),
      disabledFn: () => this.isProRataRequired,
      onChange: (mode) => {
        this.setDirty();
        if (mode === ShareholderEntryMode.PRO_RATA) {
          this.sharesEntryMode.setValue(SharesEntryMode.MANUAL);
          this.applyProRataDistribution();
        }
        this.syncDividendToForms();
        this.syncCalculationModeToForms();
      },
    });
    this.calculationMode = new RadioFieldModel<CalculationMode>({
      value: restoreCalculationMode(transactionData),
      options: [CalculationMode.AUTO, CalculationMode.MANUAL],
      idFn: identity,
      labelFn: (mode) => ({ key: `transactions.capitalReduction.calculationMode.${mode}` }),
      disabledFn: () => !this.canAutoCalculatePayment,
      onChange: () => {
        this.isManualForced = false;
        this.setDirty();
        this.syncCalculationModeToForms();
      },
    });
    this.transactionDateCardModel = new TransactionDateCardModel({
      data: shareholderData,
      value: transactionData?.registrationInNorwegianRegisterDate ?? undefined,
      onChange: (date, time) => {
        this.setDirty();
        if (date && time) {
          this.fetchState(combineDateAndTime(date, time));
        }
      },
    });
    this.resolutionDate = new LocalDateFieldModel({
      onChange: this.setDirty,
      value: transactionData?.resolutionDate?.split("T")[0],
    });

    makeAutoObservable(
      this,
      { transactionStore: false, companyData: false, shareholderData: false },
      { autoBind: true },
    );
  }

  get existingShareholders() {
    return this.shareholderData.shareholders.filter((s) => hasShares(s));
  }

  get inactiveShareholders() {
    return this.shareholderData.shareholders.filter((s) => !hasShares(s));
  }

  get hasSingleShareholder(): boolean {
    return (
      this.existingShareholders.length === 1 && !isOwnShareholder(this.existingShareholders[0])
    );
  }

  get isProRataRequired(): boolean {
    return this.purposeModel.value !== Purpose.CANCELLATION_OF_OWN && this.hasSingleShareholder;
  }

  private forceProRataWhenSingleShareholder() {
    if (this.isProRataRequired && this.isManualShareholderEntry) {
      this.shareholderEntryMode.setValue(ShareholderEntryMode.PRO_RATA);
    }
  }

  canAddShareholder() {
    return (
      this.purposeModel.value !== undefined &&
      this.purposeModel.value !== Purpose.CANCELLATION_OF_OWN
    );
  }

  changePurpose(value: Purpose | undefined) {
    this.setDirty();
    if (value === Purpose.CANCELLATION_OF_OWN) {
      this.shareholderEntryMode.setValue(ShareholderEntryMode.MANUAL);
      const ownShareholder = this.existingShareholders.find(isOwnShareholder);
      if (ownShareholder) {
        this.cancellationForms = initCancellationForms(
          ownShareholder,
          this.shareTypes,
          this.onFormSharesChange,
        );
      } else {
        this.cancellationForms = [];
      }
    } else {
      this.forceProRataWhenSingleShareholder();
    }
  }

  get shareTypes(): ShareType[] {
    return this.shareholderData.shareTypesWithShares;
  }

  get numberOfSharesBefore(): number {
    return this.shareTypeSpecifications.reduce((sum, spec) => sum + spec.availableShares, 0);
  }

  get numberOfSharesAfter(): number {
    const before = this.numberOfSharesBefore;
    const reduction = this.shareTypeSpecifications.reduce(
      (sum, spec) => sum + spec.numberOfSharesToCancel.toNumberOrZero(),
      0,
    );
    return before - reduction;
  }

  get shareCapitalBefore(): number {
    return new Big(this.numberOfSharesBefore).times(this.nominalValue).toNumber();
  }

  get nominalValue() {
    return this.transactionStore.shareNominalValue ?? 0;
  }

  get reductionInShareCapital(): number {
    const reductionInNumberOfShares = this.numberOfSharesBefore - this.numberOfSharesAfter;
    return new Big(reductionInNumberOfShares).times(this.nominalValue).toNumber();
  }

  get shareCapitalAfter(): number {
    return Big(this.shareCapitalBefore).minus(this.reductionInShareCapital).toNumber();
  }

  get ownShareholderHasNoShares(): boolean {
    const ownShareholder = this.existingShareholders.find((s) => isOwnShareholder(s));
    return (
      this.purposeModel.value === Purpose.CANCELLATION_OF_OWN &&
      (ownShareholder === undefined || !hasShares(ownShareholder))
    );
  }

  addShareholdersFromActors = (
    actors: SelectedActor[],
    onSuccess: () => void,
    onError: () => void,
  ) => {
    try {
      actors.forEach((actor) => {
        const shareholder = this.existingShareholders.find(
          (s) => actor.reference === toShareholderReference(s.id),
        );
        if (
          shareholder &&
          !this.cancellationForms.some((f) => f.shareholder.id === shareholder.id)
        ) {
          this.cancellationForms.push(
            ...initCancellationForms(shareholder, this.shareTypes, this.onFormSharesChange),
          );
        }
      });
      this.setDirty();
      onSuccess();
    } catch {
      onError();
    }
  };

  toggleRegisteredInBR() {
    this.isRegisteredInBR = !this.isRegisteredInBR;
  }

  resetValidation() {
    this.shareTypeSpecificationsError = [];
    this.cancellationFormsErrors = [];
  }

  cancellationFormsErrors: TMessage[] = [];

  validate(): boolean {
    this.resetValidation();
    this.purposeModel.validate();
    this.resolutionDate.validate();
    this.transactionDateCardModel.validate();
    this.cancellationForms.forEach((f) => f.numberOfSharesToCancel.validate());
    this.syncCalculationModeToAvailability();
    this.validateDistributionAmounts();
    this.validateReductionAmounts();
    if (
      this.shareTypeSpecifications.every((s) => s.numberOfSharesToCancel.toNumberOrZero() === 0)
    ) {
      this.shareTypeSpecificationsError.push({
        key: "reductionOfShareCapital.cancellationOfShares.shareTypeSpecifications.missing",
      });
    }
    if (this.isManualSharesEntry) {
      this.shareTypeSpecifications.forEach((s) => {
        const specified = s.numberOfSharesToCancel.toNumberOrZero();
        const distributed = this.computedTotalBySharetype(s.sharetypeId);
        const shareType = this.shareTypes.find((st) => st.sharetypeId === s.sharetypeId)!;
        if (specified !== distributed) {
          this.shareTypeSpecificationsError.push({
            key: "reductionOfShareCapital.cancellationOfShares.shareTypeSpecifications.mismatch",
            params: { sharetype: { type: "shareType", value: shareType } },
          });
        }
      });
    }
    this.validateReductionMatchesRepayment();
    if (this.cancellationForms.length === 0) {
      this.cancellationFormsErrors.push({
        key: "reductionOfShareCapital.cancellationOfShares.shareholder.missing",
      });
    }
    return this.isValid;
  }

  get isValid(): boolean {
    if (!this.purposeModel.isValid) {
      return false;
    }
    if (this.shareTypeSpecificationsError.length > 0) {
      return false;
    }
    if (this.cancellationFormsErrors.length > 0) {
      return false;
    }
    if (this.cancellationForms.some((f) => !f.numberOfSharesToCancel.isValid)) {
      return false;
    }
    if (this.cancellationForms.some((f) => !f.amountsValid)) {
      return false;
    }
    if (
      this.shareTypeSpecifications.some((s) =>
        [
          s.shareCapitalReductionAmount,
          s.sharePremiumReductionAmount,
          s.averageSharePremiumPerShare,
        ].some((f) => !f.isValid),
      )
    ) {
      return false;
    }
    return this.resolutionDate.isValid && this.transactionDateCardModel.isValid;
  }

  get canAutoCalculatePayment(): boolean {
    return this.shareholderData.canAutoCalculatePayment;
  }

  private isManualForced = false;

  private syncCalculationModeToAvailability() {
    if (!this.canAutoCalculatePayment) {
      if (this.calculationMode.value === CalculationMode.AUTO) {
        this.calculationMode.setValue(CalculationMode.MANUAL);
        this.isManualForced = true;
      }
    } else if (this.isManualForced) {
      this.calculationMode.setValue(CalculationMode.AUTO);
      this.isManualForced = false;
    }
  }

  get isAutoMode(): boolean {
    return this.canAutoCalculatePayment && this.calculationMode.value === CalculationMode.AUTO;
  }

  private syncCalculationModeToForms() {
    this.cancellationForms.forEach((f) => {
      f.entersDividend = !this.isAutoMode && this.allowsDividend;
    });
  }

  // Left optional: a company that does not have the amounts to hand can register the cancellation without them, and
  // the statement asks for what posts 5 and 6 are missing when it comes to report them.
  private validateReductionAmounts() {
    this.shareTypeSpecifications.forEach((s) => {
      s.sharePremiumReductionAmount.validate();
      s.shareCapitalReductionAmount.validate();
      s.averageSharePremiumPerShare.validate();
    });
  }

  private validateDistributionAmounts() {
    if (this.purposeModel.value !== Purpose.DISTRIBUTION) {
      return;
    }
    this.syncCalculationModeToForms();
    this.syncDividendToForms();
    this.cancellationForms.forEach((f) => {
      f.amountPerShare.validate();
      f.totalAmount.validate();
      if (!this.isAutoMode && this.allowsDividend) {
        f.dividendAmount.validate();
      }
    });
  }

  clearDirty() {
    this.isDirty = false;
  }

  setDirty() {
    this.isDirty = true;
  }

  removeShareholder(shareholderId: number) {
    this.cancellationForms = this.cancellationForms.filter(
      (f) => f.shareholder.id !== shareholderId,
    );
  }

  get excludedShareholdersForCancel(): string[] {
    return [
      ...this.inactiveShareholders.map((s) => toShareholderReference(s.id)),
      ...this.groupedCancellationForms.map(([id]) => toShareholderReference(id)),
    ];
  }

  get sortedCancellationForms(): ShareholderCancellationModel[] {
    return [...this.cancellationForms].sort((a, b) =>
      a.shareholder.name.localeCompare(b.shareholder.name),
    );
  }

  get groupedCancellationForms(): [number, ShareholderCancellationModel[]][] {
    const groups = new Map<number, ShareholderCancellationModel[]>();
    for (const f of this.cancellationForms) {
      const id = f.shareholder.id;
      const group = groups.get(id);
      if (group) group.push(f);
      else groups.set(id, [f]);
    }
    return [...groups.entries()];
  }

  get isManualSharesEntry(): boolean {
    return this.sharesEntryMode.value === SharesEntryMode.MANUAL;
  }

  get isManualShareholderEntry(): boolean {
    return this.shareholderEntryMode.value === ShareholderEntryMode.MANUAL;
  }

  // An unevenly distributed cancellation is a skjevdelt partiell likvidasjon: the whole payment is a realisation,
  // reported as a single total consideration. None of it is a dividend, and how much of it repaid paid-in capital
  // is a fact no post reads, so the breakdown is not asked for at all.
  get allowsDividend(): boolean {
    return !this.isManualShareholderEntry;
  }

  // Only the forms that are not already zero, so a repeated sync cannot re-derive a total the user has since edited.
  private syncDividendToForms() {
    if (this.allowsDividend) return;
    this.cancellationForms
      .filter((f) => f.dividendAmount.parsedValue !== 0)
      .forEach((f) => f.dividendAmount.setAndCommitInputValue(0));
  }

  applyProRataDistribution() {
    const eligible =
      this.purposeModel.value === Purpose.CANCELLATION_OF_OWN
        ? this.existingShareholders.filter(isOwnShareholder)
        : this.existingShareholders.filter((s) => !isOwnShareholder(s));

    this.cancellationForms = eligible.flatMap((shareholder) =>
      initCancellationForms(shareholder, this.shareTypes, this.onFormSharesChange),
    );

    this.shareTypeSpecifications.forEach((spec) => {
      const totalToCancel = spec.numberOfSharesToCancel.toNumberOrZero();
      if (totalToCancel === 0 || spec.availableShares === 0) return;

      const formsForType = this.cancellationForms.filter((f) => f.sharetypeId === spec.sharetypeId);
      const holderShares = formsForType.map((f) => f.availableShares);
      const allocations = distributeProRata(totalToCancel, holderShares);
      formsForType.forEach((form, i) => {
        form.numberOfSharesToCancel.setAndCommitInputValue(allocations[i]);
      });
    });
  }

  // What an evenly distributed cancellation takes out of the two paid-in accounts is what it paid out of them, so
  // the two amounts together are the payment less the dividend. An unevenly distributed cancellation, and one
  // deleting the company's own shares, carry their capital away whatever was paid, and the two are unrelated.
  private validateReductionMatchesRepayment() {
    if (
      this.isAutoMode ||
      this.isManualShareholderEntry ||
      this.purposeModel.value === Purpose.CANCELLATION_OF_OWN
    ) {
      return;
    }
    this.shareTypeSpecifications
      .filter(
        (s) => !s.shareCapitalReductionAmount.isEmpty || !s.sharePremiumReductionAmount.isEmpty,
      )
      .forEach((s) => {
        const stated =
          (s.shareCapitalReductionAmount.parsedValue ?? 0) +
          (s.sharePremiumReductionAmount.parsedValue ?? 0);
        const repaid = this.repaidPaidInCapitalBySharetype(s.sharetypeId);
        if (Big(stated).round(2).eq(Big(repaid).round(2))) {
          return;
        }
        const shareType = this.shareTypes.find((st) => st.sharetypeId === s.sharetypeId)!;
        this.shareTypeSpecificationsError.push({
          key: "transactions.capitalReduction.reductionDoesNotMatchRepayment",
          params: { sharetype: { type: "shareType", value: shareType } },
        });
      });
  }

  private repaidPaidInCapitalBySharetype(sharetypeId: number): number {
    return sumBy(
      this.cancellationForms.filter((f) => f.sharetypeId === sharetypeId),
      (f) => (f.totalAmount.parsedValue ?? 0) - (f.dividendAmount.parsedValue ?? 0),
    );
  }

  computedTotalBySharetype(sharetypeId: number): number {
    return sumBy(
      this.cancellationForms.filter((f) => f.sharetypeId === sharetypeId),
      (f) => f.numberOfSharesToCancel.toNumberOrZero(),
    );
  }

  onFormSharesChange(sharetypeId: number) {
    if (!this.isManualSharesEntry) this.syncSharetypeSpec(sharetypeId);
  }

  syncSharetypeSpec(sharetypeId: number) {
    const spec = this.shareTypeSpecifications.find((s) => s.sharetypeId === sharetypeId);
    spec?.numberOfSharesToCancel.setAndCommitInputValue(this.computedTotalBySharetype(sharetypeId));
  }

  syncAllSharetypeSpecs() {
    this.shareTypeSpecifications.forEach((s) => this.syncSharetypeSpec(s.sharetypeId));
  }

  get orgNo() {
    return this.companyData.orgNumber;
  }

  *fetchState(date: string) {
    yield this.transactionStore.fetchInitialTransactionState(this.orgNo, date);
    yield this.shareholderData.loadShareholdersForDate(this.orgNo, dateBefore(date));
    this.syncCalculationModeToAvailability();
    this.forceProRataWhenSingleShareholder();

    this.shareTypeSpecifications = mapToSharetypeSpecifications(
      this.shareholderData.shareTypesWithShares,
      this.shareholderData.shareSumBySharetypeCode,
      this.nominalValue,
      () => {
        if (!this.isManualShareholderEntry) this.applyProRataDistribution();
      },
    );

    if (this.transactionData) {
      this.transactionData.shareholderSharetypeSpecifications.forEach((spec) => {
        const model = this.shareTypeSpecifications.find((s) => s.sharetypeId === spec.shareTypeId);
        model?.numberOfSharesToCancel.setAndCommitInputValue(spec.numberOfShares);
        if (spec.shareCapitalReductionAmount !== undefined) {
          model?.shareCapitalReductionAmount.setAndCommitInputValue(
            spec.shareCapitalReductionAmount,
          );
        }
        if (spec.sharePremiumReductionAmount !== undefined) {
          model?.sharePremiumReductionAmount.setAndCommitInputValue(
            spec.sharePremiumReductionAmount,
          );
        }
        if (spec.averageSharePremiumPerShare !== undefined) {
          model?.averageSharePremiumPerShare.setAndCommitInputValue(
            spec.averageSharePremiumPerShare,
          );
        }
      });
      this.cancellationForms = [];
      this.transactionData.shareholderCancellationSpecifications.forEach((cancelSpec) => {
        const shareholder = this.existingShareholders.find(
          (s) => s.id === cancelSpec.shareholderId,
        );
        if (!shareholder) return;
        const initialValues = new Map(
          cancelSpec.shareholderSharetypeSpecifications.map((spec) => [
            spec.shareTypeId,
            {
              numberOfSharesToCancel: spec.numberOfShares,
              totalAmount: spec.totalAmount,
              dividendAmount: spec.dividendAmount,
            },
          ]),
        );
        const forms = initCancellationForms(
          shareholder,
          this.shareTypes,
          this.onFormSharesChange,
          initialValues,
        );
        this.cancellationForms.push(...forms);
      });
    }
  }

  get isFetching() {
    return isPending(this.transactionStore.fetchInitialStateResult);
  }

  prepareData(): CancellationOfShares {
    const isDistribution = this.purposeModel.value === Purpose.DISTRIBUTION;
    const isAutoCalculate = this.isAutoMode;
    return {
      organizationnumber: this.orgNo,
      shareReductionPurpose: this.purposeModel.value ?? "",
      isCancellationProRata: !this.isManualShareholderEntry,
      isAutoCalculate,
      resolutionDate: this.resolutionDate.validatedValue
        ? combineDateAndTime(this.resolutionDate.validatedValue, "12:00")
        : undefined,
      isRegisteredInNorwegianBusinessEnterprise: true,
      registrationInNorwegianRegisterDate: this.transactionDateCardModel.combinedDateAndTime,
      shareholderSharetypeSpecifications: this.shareTypeSpecifications
        .filter((s) => s.numberOfSharesToCancel.toNumberOrZero() > 0)
        .map((share) => ({
          shareTypeId: share.sharetypeId,
          numberOfShares: share.numberOfSharesToCancel.toNumberOrZero(),
          shareCapitalReductionAmount: !this.isAutoMode
            ? share.shareCapitalReductionAmount.parsedValue
            : undefined,
          sharePremiumReductionAmount: !this.isAutoMode
            ? share.sharePremiumReductionAmount.parsedValue
            : undefined,
          averageSharePremiumPerShare: !this.isAutoMode
            ? share.averageSharePremiumPerShare.parsedValue
            : undefined,
        })),
      shareholderCancellationSpecifications: this.groupedCancellationForms.map(
        ([shareholderId, forms]) => ({
          shareholderId,
          shareholderSharetypeSpecifications: forms
            .filter((f) => f.numberOfSharesToCancel.toNumberOrZero() > 0)
            .map((f) => ({
              shareTypeId: f.sharetypeId,
              numberOfShares: f.numberOfSharesToCancel.toNumberOrZero(),
              totalAmount: isDistribution ? f.totalAmount.parsedValue : undefined,
              dividendAmount:
                !isDistribution || isAutoCalculate
                  ? undefined
                  : this.allowsDividend
                    ? f.dividendAmount.parsedValue
                    : 0,
            })),
        }),
      ),
    };
  }
}

export class ShareTypeSpecificationModel {
  numberOfSharesToCancel: NumberFieldModel;
  shareCapitalReductionAmount: NumberFieldModel;
  sharePremiumReductionAmount: NumberFieldModel;
  averageSharePremiumPerShare: NumberFieldModel;

  constructor(
    readonly sharetypeId: number,
    readonly shareType: ShareType,
    readonly availableShares: number,
    readonly nominalValue: number,
    onChange?: () => void,
  ) {
    this.shareCapitalReductionAmount = new NumberFieldModel({ isPositiveOrZero: true });
    this.sharePremiumReductionAmount = new NumberFieldModel({ isPositiveOrZero: true });
    this.averageSharePremiumPerShare = new NumberFieldModel({ isPositiveOrZero: true });
    this.numberOfSharesToCancel = new NumberFieldModel({
      isInteger: true,
      isPositiveOrZero: true,
      onChange,
      validator: (value) => {
        return value !== undefined && value > this.availableShares
          ? {
              key: "number-field.smaller-or-equal-than-n",
              params: { n: { type: "number", value: this.availableShares } },
            }
          : undefined;
      },
    });
    makeAutoObservable(this);
  }

  get numberOfSharesAfter(): number {
    return this.availableShares - this.numberOfSharesToCancel.toNumberOrZero();
  }

  get shareCapitalBefore(): number {
    return new Big(this.availableShares).times(this.nominalValue).toNumber();
  }

  get shareCapitalAfter(): number {
    const reductionInCapital = new Big(this.numberOfSharesToCancel.toNumberOrZero())
      .times(this.nominalValue)
      .toNumber();
    return this.shareCapitalBefore - reductionInCapital;
  }
}

export class ShareholderCancellationModel {
  numberOfSharesToCancel: NumberFieldModel;
  amountPerShare: NumberFieldModel;
  totalAmount: NumberFieldModel;
  dividendAmount: NumberFieldModel;
  entersDividend = true;
  // Prevents the amountPerShare <-> totalAmount recalculations from triggering each other recursively.
  private isSyncingAmounts = false;

  constructor(
    readonly shareholder: ExtendedShareholderDTO,
    readonly sharetypeId: number,
    readonly shareType: ShareType,
    readonly availableShares: number,
    onChange?: () => void,
    initialValues?: {
      numberOfSharesToCancel?: number;
      totalAmount?: number;
      dividendAmount?: number;
    },
  ) {
    this.numberOfSharesToCancel = new NumberFieldModel({
      isInteger: true,
      isPositiveOrZero: true,
      onChange: () => {
        onChange?.();
        this.recalculateTotalAmount();
      },
      value: initialValues?.numberOfSharesToCancel,
      validator: (value) => {
        return value !== undefined && value > this.availableShares
          ? {
              key: "number-field.smaller-or-equal-than-n",
              params: { n: { type: "number", value: this.availableShares } },
            }
          : undefined;
      },
    });
    this.amountPerShare = new NumberFieldModel({
      isPositiveOrZero: true,
      onChange: () => {
        onChange?.();
        this.recalculateTotalAmount();
      },
      value:
        initialValues?.totalAmount !== undefined && initialValues?.numberOfSharesToCancel
          ? new Big(initialValues.totalAmount).div(initialValues.numberOfSharesToCancel).toNumber()
          : undefined,
    });
    this.totalAmount = new NumberFieldModel({
      isPositiveOrZero: true,
      onChange: () => {
        onChange?.();
        this.recalculateAmountPerShare();
      },
      value: initialValues?.totalAmount,
      validator: (value) => this.validateTotalAmount(value),
    });
    this.dividendAmount = new NumberFieldModel({
      isPositiveOrZero: true,
      onChange,
      value: initialValues?.dividendAmount,
    });
    makeAutoObservable(this);
  }

  recalculateTotalAmount() {
    if (this.isSyncingAmounts) {
      return;
    }
    this.isSyncingAmounts = true;
    try {
      const amountPerShare = this.amountPerShare.parsedValue;
      this.totalAmount.setAndCommitInputValue(
        amountPerShare === undefined
          ? undefined
          : new Big(this.numberOfSharesToCancel.toNumberOrZero()).times(amountPerShare).toNumber(),
      );
    } finally {
      this.isSyncingAmounts = false;
    }
  }

  recalculateAmountPerShare() {
    if (this.isSyncingAmounts) {
      return;
    }
    this.isSyncingAmounts = true;
    try {
      const totalAmount = this.totalAmount.parsedValue;
      const numberOfShares = this.numberOfSharesToCancel.toNumberOrZero();
      this.amountPerShare.setAndCommitInputValue(
        totalAmount === undefined || numberOfShares === 0
          ? undefined
          : new Big(totalAmount).div(numberOfShares).toNumber(),
      );
    } finally {
      this.isSyncingAmounts = false;
    }
  }

  private validateTotalAmount(value: number | undefined): TMessage | undefined {
    const dividend = this.dividendAmount.parsedValue;
    if (!this.entersDividend || dividend === undefined || value === undefined) {
      return undefined;
    }
    return new Big(dividend).lte(value)
      ? undefined
      : { key: "transactions.capitalReduction.dividendExceedsTotal" };
  }

  get id(): string {
    return `${this.shareholder.id}-${this.sharetypeId}`;
  }

  get amountsValid(): boolean {
    const dividend = this.entersDividend ? [this.dividendAmount] : [];
    return [this.amountPerShare, this.totalAmount, ...dividend].every((field) => field.isValid);
  }
}

function initCancellationForms(
  shareholder: ExtendedShareholderDTO,
  shareTypes: ShareType[],
  onSharesChange?: (sharetypeId: number) => void,
  initialValuesBySharetype?: Map<
    number,
    {
      numberOfSharesToCancel?: number;
      totalAmount?: number;
      dividendAmount?: number;
    }
  >,
): ShareholderCancellationModel[] {
  return shareTypes.map(
    (st) =>
      new ShareholderCancellationModel(
        shareholder,
        st.sharetypeId,
        st,
        shareholder.calculatedShares[st.shareTypeCode] ?? 0,
        onSharesChange ? () => onSharesChange(st.sharetypeId) : undefined,
        initialValuesBySharetype?.get(st.sharetypeId),
      ),
  );
}

export function mapToSharetypeSpecifications(
  sharetypes: ShareType[],
  shareSumBySharetypeCode: {
    [shareTypeCode in ShareTypeCode]?: number;
  },
  nominalValue: number,
  onChange?: () => void,
): ShareTypeSpecificationModel[] {
  return Object.entries(shareSumBySharetypeCode).map(([code, number]) => {
    const shareType = sharetypes.find((s) => s.shareTypeCode === code)!;
    return new ShareTypeSpecificationModel(
      shareType.sharetypeId,
      shareType,
      number,
      nominalValue,
      onChange,
    );
  });
}

// Largest remainder method: distributes totalToCancel integer shares across holders
// proportionally to their ownership, with no rounding error in the total.
export function distributeProRata(totalToCancel: number, holderShares: number[]): number[] {
  const totalAvailable = sumBy(holderShares, identity);
  const exact = holderShares.map((shares) => (totalToCancel * shares) / totalAvailable);
  const result = exact.map(Math.floor); // each holder gets at least their floor
  const remainder = totalToCancel - sumBy(result, identity); // shares lost to flooring
  // give the leftover shares to holders with the largest fractional parts
  exact
    .map((e, i) => ({ i, frac: e % 1 }))
    .sort((a, b) => b.frac - a.frac)
    .slice(0, remainder)
    .forEach(({ i }) => result[i]++);
  return result;
}

export function filterShareTypesWithShares(
  sharetypes: ShareType[],
  shareSumBySharetypeCode: { [shareTypeCode in ShareTypeCode]?: number },
): ShareType[] {
  return sharetypes.filter(
    (sharetype) => (shareSumBySharetypeCode[sharetype.shareTypeCode] ?? 0) > 0,
  );
}
